import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { SealCheck, ThumbsUp, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import '../styles/PokemonCardsRequestModal.css';
import { useLanguage } from '../i18n/LanguageContext';

const requestLangFor = (language) => (language === 'fr' ? 'fr' : 'en');

const ITEMS_PER_PAGE = 10;
const buildImageUrl = (uri) => (uri ? `${uri}/high.webp` : null);

const PokemonCardsRequestModal = ({
    open,
    onClose,
    initialSearch,
    onSuccess
}) => {
    const { t, language } = useLanguage();
    const requestLang = requestLangFor(language);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [lastSearchTerm, setLastSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [currentQuery, setCurrentQuery] = useState('');
    const [realisableIds, setRealisableIds] = useState(() => new Set());
    const [blockedIds, setBlockedIds] = useState(() => new Set());
    const [selectedCards, setSelectedCards] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        message: ''
    });
    const [formError, setFormError] = useState('');

    const selectedList = useMemo(
        () => Object.values(selectedCards),
        [selectedCards]
    );
    const selectedCount = selectedList.length;
    const formName = 'cards-request';
    const subjectLine = t('cards.request.form.subject');
    const linkBase = useMemo(
        () => `https://api.tcgdex.net/v2/${requestLang}/cards`,
        [requestLang]
    );
    const selectedCardLines = useMemo(() => {
        return selectedList.map((card) => {
            const [setCodeRaw] = card.id.split('-');
            const setCode = setCodeRaw?.toUpperCase();
            const label = setCode
                ? `${card.name} (${setCode} #${card.localId})`
                : `${card.name} (#${card.localId})`;
            return `- ${label} ⋅ ${linkBase}/${card.id}`;
        });
    }, [linkBase, selectedList]);
    const selectedCardsPayload = selectedCardLines.join('\n');
    const initialSearchRef = useRef('');

    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setSearchResults([]);
            setSearchLoading(false);
            setSearchError('');
            setLastSearchTerm('');
            setCurrentPage(1);
            setHasMore(false);
            setCurrentQuery('');
            setSelectedCards({});
            setFormData({ email: '', message: '' });
            setFormError('');
            initialSearchRef.current = '';
        }
    }, [open]);

    const fetchRealisableCards = useCallback(async () => {
        try {
            const response = await fetch(
                '/.netlify/functions/realizable-cards'
            );
            if (!response.ok) {
                throw new Error('Unable to load realizable cards.');
            }
            const payload = await response.json();
            const entries = Array.isArray(payload) ? payload : [];
            setRealisableIds(
                new Set(
                    entries
                        .filter((entry) => entry.status === 'realizable')
                        .map((entry) => entry.card_id)
                )
            );
            setBlockedIds(
                new Set(
                    entries
                        .filter((entry) => entry.status === 'blocked')
                        .map((entry) => entry.card_id)
                )
            );
        } catch (error) {
            console.error('Error fetching realizable cards', error);
        }
    }, []);

    useEffect(() => {
        fetchRealisableCards();
    }, [fetchRealisableCards]);

    const runSearch = useCallback(
        async (value, page = 1, append = false) => {
            const trimmedQuery = value.trim();
            if (!trimmedQuery) {
                setSearchError(t('cards.request.searchEmpty'));
                setSearchResults([]);
                setLastSearchTerm('');
                setHasMore(false);
                return;
            }

            const url = new URL(
                `https://api.tcgdex.net/v2/${requestLang}/cards`
            );
            url.searchParams.set('name', `like:${trimmedQuery}`);
            url.searchParams.set(
                'pagination:itemsPerPage',
                ITEMS_PER_PAGE.toString()
            );
            url.searchParams.set('pagination:page', page.toString());

            setSearchError('');
            setSearchLoading(true);
            setLastSearchTerm(trimmedQuery);
            if (!append) {
                setSearchResults([]);
                setCurrentQuery(trimmedQuery);
            }

            try {
                const response = await fetch(url.toString());
                if (!response.ok) {
                    throw new Error(response.statusText || 'HTTP Error');
                }
                const data = await response.json();
                const items = Array.isArray(data) ? data : [];
                setSearchResults((prev) =>
                    append ? [...prev, ...items] : items
                );
                setCurrentPage(page);
                setHasMore(items.length === ITEMS_PER_PAGE);
            } catch (error) {
                const message =
                    error instanceof Error ? error.message : 'Unknown error';
                setSearchError(
                    t('cards.request.searchError', {
                        message
                    })
                );
            } finally {
                setSearchLoading(false);
            }
        },
        [requestLang, t]
    );

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        runSearch(searchQuery);
    };

    const visibleSearchResults = useMemo(
        () => searchResults.filter((card) => !blockedIds.has(card.id)),
        [blockedIds, searchResults]
    );

    useEffect(() => {
        if (
            open &&
            initialSearch &&
            initialSearch !== initialSearchRef.current
        ) {
            initialSearchRef.current = initialSearch;
            setSearchQuery(initialSearch);
            runSearch(initialSearch);
        }
    }, [initialSearch, open, runSearch]);

    const toggleCardSelection = (card) => {
        setFormError('');
        setSelectedCards((prev) => {
            if (prev[card.id]) {
                const next = { ...prev };
                delete next[card.id];
                return next;
            }
            return { ...prev, [card.id]: card };
        });
    };

    const handleFieldChange = (field) => (event) => {
        const value = event.target.value;
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const hasEmail = Boolean(formData.email.trim());
    const hasSelection = selectedCount > 0;
    const isReadyToSubmit = hasEmail && hasSelection;

    const handleContactSubmit = async (event) => {
        event.preventDefault();
        if (selectedCount === 0) {
            setFormError(t('cards.request.form.emptySelection'));
            return;
        }
        if (!hasEmail) {
            setFormError(t('cards.request.form.emailRequired'));
            return;
        }
        if (isSubmitting) {
            return;
        }

        setFormError('');
        setIsSubmitting(true);

        try {
            const isLocalhost =
                typeof window !== 'undefined' &&
                ['localhost', '127.0.0.1', '0.0.0.0'].includes(
                    window.location.hostname
                );

            if (isLocalhost) {
                await new Promise((resolve) => setTimeout(resolve, 500));
            } else {
                const formDataForNetlify = new FormData(event.target);
                formDataForNetlify.set('selectedCards', selectedCardsPayload);
                const response = await fetch('/', {
                    method: 'POST',
                    body: formDataForNetlify
                });
                if (!response.ok) {
                    throw new Error('Submission failed');
                }
            }

            if (typeof onSuccess === 'function') {
                onSuccess(t('cards.request.toastSuccess'));
            }
            onClose();
        } catch (error) {
            console.error(error);
            const message =
                error instanceof Error ? error.message : 'Unknown error';
            setFormError(
                t('cards.request.form.submitError', {
                    message
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        onClose();
    };
    return (
        <Modal
            open={open}
            onClose={handleClose}
            className='cards-request-modal'
            aria-labelledby='cards-request-modal-title'
        >
            <div className='cards-request-dialog'>
                <header className='cards-request-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('cards.request.eyebrow')}
                            </p>
                            <h2
                                id='cards-request-modal-title'
                                className='cards-request-title'
                            >
                                {t('cards.request.title')}
                            </h2>
                            <p className='cards-request-lead'>
                                {t('cards.request.lead')}
                            </p>
                            <div className='cards-request-warning' role='note'>
                                <span className='cards-request-warning-icon'>
                                    <WarningCircleIcon size={18} weight='bold' />
                                </span>
                                <p>{t('cards.request.warning')}</p>
                            </div>
                        </div>

                    <button
                        type='button'
                        className='cards-request-close'
                        onClick={handleClose}
                        aria-label={t('cards.request.openButton')}
                    >
                        <XIcon size={16} weight='regular' />
                    </button>
                </header>
                <div className='cards-request-body'>
                    <section className='cards-request-search-section'>
                        <form
                            className='cards-request-search-form'
                            onSubmit={handleSearchSubmit}
                        >
                            <label
                                className='cards-request-label'
                                htmlFor='modal-tcgdex-search'
                            >
                                {t('cards.request.searchLabel')}
                            </label>
                            <div className='cards-request-input-row'>
                                <input
                                    id='modal-tcgdex-search'
                                    type='text'
                                    className='cards-request-input'
                                    value={searchQuery}
                                    onChange={(event) =>
                                        setSearchQuery(event.target.value)
                                    }
                                    placeholder={t(
                                        'cards.request.searchPlaceholder'
                                    )}
                                />
                                <button className='button' type='submit'>
                                    {t('cards.request.searchButton')}
                                </button>
                            </div>
                            <p className='cards-request-note'>
                                {t('cards.request.selectionInstruction')}
                            </p>
                        </form>
                        {searchError ? (
                            <p className='cards-request-error'>
                                {searchError}
                            </p>
                        ) : null}
                        {searchLoading ? (
                            <p className='cards-request-note'>
                                {t('cards.request.searching')}
                            </p>
                        ) : null}
                        {visibleSearchResults.length > 0 ? (
                            <>
                                {realisableIds.size > 0 ? (
                                    <div className='cards-request-legend'>
                                        <ThumbsUp size={16} weight='fill' />
                                        <span>
                                            {t(
                                                'cards.request.realisableLegend'
                                            )}
                                        </span>
                                    </div>
                                ) : null}
                                <div className='cards-request-results-grid'>
                                    {visibleSearchResults
                                        .map((card) => ({
                                            card,
                                            imageUrl: buildImageUrl(card.image)
                                        }))
                                        .filter(({ imageUrl }) => Boolean(imageUrl))
                                        .map(({ card, imageUrl }) => {
                                            const isSelected = Boolean(
                                                selectedCards[card.id]
                                            );
                                            const isMarked = realisableIds.has(
                                                card.id
                                            );
                                            return (
                                                <button
                                                    key={card.id}
                                                    type='button'
                                                    className={`cards-request-image ${
                                                        isSelected
                                                            ? 'is-selected'
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        toggleCardSelection(
                                                            card
                                                        )
                                                    }
                                                    aria-pressed={isSelected}
                                                    aria-label={`${card.name} #${card.localId}`}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={card.name}
                                                        loading='lazy'
                                                        width='154'
                                                        height='216'
                                                    />
                                                    <div className='cards-request-selected-overlay'>
                                                        {isSelected ? (
                                                            <div className='cards-request-selected-badge'>
                                                                <SealCheck
                                                                    size={32}
                                                                    weight='fill'
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    {isMarked && (
                                                        <span className='cards-request-marked-pip'>
                                                            <ThumbsUp
                                                                size={14}
                                                                weight='fill'
                                                            />
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                </div>
                            </>
                        ) : lastSearchTerm &&
                          !searchLoading &&
                          !searchError ? (
                            <p className='cards-request-note'>
                                {t('cards.request.noResults', {
                                    query: lastSearchTerm
                                })}
                            </p>
                        ) : null}
                                {hasMore ? (
                                    <button
                                        type='button'
                                        className='button button-outline cards-request-load-more'
                                        onClick={() =>
                                            runSearch(currentQuery, currentPage + 1, true)
                                        }
                                        disabled={searchLoading}
                                    >
                                        {searchLoading
                                            ? t('cards.request.searching')
                                            : t('cards.request.loadMore')}
                                    </button>
                                ) : null}
                    </section>
                    <section className='cards-request-details'>
                        <div className='cards-request-selection-summary'>
                            <div className='cards-request-selection-header'>
                                <p className='eyebrow'>
                                    {t('cards.request.selectedCards')}
                                </p>
                                <span className='cards-request-selection-count'>
                                    {selectedCount}
                                </span>
                            </div>
                            <div className='cards-request-selection-list'>
                                {selectedList.length > 0 ? (
                                    selectedList.map((card) => (
                                        <div
                                            className='cards-request-chip'
                                            key={card.id}
                                        >
                                            <span>{card.name}</span>
                                            <button
                                                type='button'
                                                onClick={() =>
                                                    toggleCardSelection(card)
                                                }
                                                aria-label={t(
                                                    'cards.request.removeCard',
                                                    { name: card.name }
                                                )}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <p className='cards-request-note'>
                                        {t('cards.request.selectionEmpty')}
                                    </p>
                                )}
                            </div>
                        </div>
                        <form
                            className='cards-request-cta'
                            name={formName}
                            method='POST'
                            data-netlify='true'
                            netlify-honeypot='bot-field'
                            onSubmit={handleContactSubmit}
                        >
                            <input
                                type='hidden'
                                name='form-name'
                                value={formName}
                            />
                            <input type='hidden' name='subject' value={subjectLine} />
                            <input type='hidden' name='language' value={language} />
                            <input
                                type='hidden'
                                name='selectedCards'
                                value={selectedCardsPayload}
                            />
                            <div style={{ display: 'none' }}>
                                <label>
                                    {t('cards.request.form.botFieldLabel')}
                                    <input name='bot-field' />
                                </label>
                            </div>
                            <div className='cards-request-cta-fields'>
                                <p className='eyebrow'>
                                    {t('cards.request.form.eyebrow')}
                                </p>
                                <label htmlFor='modal-contact-email'>
                                    {t('cards.request.form.emailLabel')}
                                </label>
                                <input
                                    id='modal-contact-email'
                                    name='email'
                                    type='email'
                                    className='cards-request-input'
                                    value={formData.email}
                                    onChange={handleFieldChange('email')}
                                />
                                <label htmlFor='modal-contact-message'>
                                    {t('cards.request.form.messageLabel')}
                                </label>
                                <textarea
                                    id='modal-contact-message'
                                    name='message'
                                    className='cards-request-input cards-request-textarea'
                                    value={formData.message}
                                    onChange={handleFieldChange('message')}
                                />
                            </div>
                            <div className='cards-request-cta-actions'>
                                <button
                                    className='button'
                                    type='submit'
                                    disabled={!isReadyToSubmit || isSubmitting}
                                    aria-busy={isSubmitting}
                                >
                                    {t('cards.request.form.submit')}
                                </button>
                            </div>
                            {formError ? (
                                <p className='cards-request-error cards-request-error-inline cards-request-error-row'>
                                    {formError}
                                </p>
                            ) : null}
                        </form>
                    </section>
                </div>
            </div>
        </Modal>
    );
};

export default PokemonCardsRequestModal;
